import { useState } from 'react';
import {
  Box,
  Typography,
  Switch,
  Collapse,
  alpha,
  useTheme
} from '@mui/material';
import InventoryIcon from '@mui/icons-material/Inventory';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import SettingsIcon from '@mui/icons-material/Settings';

const InventoryPermissions = ({
  permissions,
  roleForm,
  setRoleForm
}) => {
  const theme = useTheme();
  const [expandedSections, setExpandedSections] = useState({
    inventory_page: false,
    inventory_transactions: false,
    inventory_configurations: false
  });

  const sections = [
    {
      key: 'inventory_page',
      label: 'Inventory Page',
      permissionPrefix: 'inventory.inventory',
      exactOnly: true,
      icon: <InventoryIcon sx={{ fontSize: 18, color: theme.palette.primary.main }} />
    },
    {
      key: 'inventory_transactions',
      label: 'Inventory Transactions Page',
      permissionPrefix: 'inventory.inventory.transactions',
      exactOnly: false,
      viewOnly: true,
      icon: <SwapHorizIcon sx={{ fontSize: 18, color: theme.palette.primary.main }} />
    },
    {
      key: 'inventory_configurations',
      label: 'Inventory Configurations Page',
      permissionPrefix: 'inventory.inventory.configurations',
      exactOnly: false,
      icon: <SettingsIcon sx={{ fontSize: 18, color: theme.palette.primary.main }} />
    }
  ];

  const renderSection = (section) => {
    const isExpanded = expandedSections[section.key] !== false;

    const sectionPermissions = permissions.filter(p => {
      if (!p.name) return false;
      if (section.exactOnly) {
        const suffix = p.name.slice((section.permissionPrefix + '.').length);
        return p.name.startsWith(section.permissionPrefix + '.') && ['view', 'edit', 'delete'].includes(suffix);
      }
      return p.name.startsWith(section.permissionPrefix + '.');
    });

    if (sectionPermissions.length === 0) return null;

    const viewPerm = sectionPermissions.find(p => p.name.endsWith('.view'));
    const editPerm = sectionPermissions.find(p => p.name.endsWith('.edit'));
    const deletePerm = sectionPermissions.find(p => p.name.endsWith('.delete'));

    return (
      <Box
        key={section.key}
        sx={{
          mb: 2,
          border: `1px solid ${alpha(theme.palette.grey[400], 0.5)}`,
          borderRadius: 2,
          overflow: 'hidden',
          background: 'white'
        }}
      >
        <Box
          onClick={() => setExpandedSections(prev => ({ ...prev, [section.key]: !isExpanded }))}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 2,
            cursor: 'pointer',
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.03)}, ${alpha(theme.palette.primary.main, 0.01)})`,
            borderBottom: isExpanded ? `1px solid ${alpha(theme.palette.grey[400], 0.3)}` : 'none',
            '&:hover': {
              background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.06)}, ${alpha(theme.palette.primary.main, 0.03)})`
            }
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            {section.icon}
            <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.primary' }}>
              {section.label}
            </Typography>
          </Box>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {isExpanded ? '▼' : '▶'}
          </Typography>
        </Box>

        <Collapse in={isExpanded}>
          <Box sx={{ p: 2 }}>
            {viewPerm && (
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                py: 1,
                borderBottom: section.viewOnly ? 'none' : `1px solid ${alpha(theme.palette.grey[300], 0.5)}`
              }}>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>View</Typography>
                  <Typography variant="caption" color="text.secondary">Can view this page</Typography>
                </Box>
                <Switch
                  checked={roleForm.permissions[viewPerm.id]?.read || false}
                  onChange={(e) => {
                    const newPermissions = { ...roleForm.permissions };
                    if (!newPermissions[viewPerm.id]) newPermissions[viewPerm.id] = {};
                    newPermissions[viewPerm.id].read = e.target.checked;
                    setRoleForm({ ...roleForm, permissions: newPermissions });
                  }}
                  size="small"
                />
              </Box>
            )}

            {!section.viewOnly && editPerm && (
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                py: 1,
                borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.5)}`
              }}>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>Edit</Typography>
                  <Typography variant="caption" color="text.secondary">Can edit data on this page</Typography>
                </Box>
                <Switch
                  checked={roleForm.permissions[editPerm.id]?.write || false}
                  onChange={(e) => {
                    const newPermissions = { ...roleForm.permissions };
                    if (!newPermissions[editPerm.id]) newPermissions[editPerm.id] = {};
                    newPermissions[editPerm.id].write = e.target.checked;
                    setRoleForm({ ...roleForm, permissions: newPermissions });
                  }}
                  size="small"
                />
              </Box>
            )}

            {!section.viewOnly && deletePerm && (
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                py: 1
              }}>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>Delete</Typography>
                  <Typography variant="caption" color="text.secondary">Can delete data on this page</Typography>
                </Box>
                <Switch
                  checked={roleForm.permissions[deletePerm.id]?.delete || false}
                  onChange={(e) => {
                    const newPermissions = { ...roleForm.permissions };
                    if (!newPermissions[deletePerm.id]) newPermissions[deletePerm.id] = {};
                    newPermissions[deletePerm.id].delete = e.target.checked;
                    setRoleForm({ ...roleForm, permissions: newPermissions });
                  }}
                  size="small"
                />
              </Box>
            )}
          </Box>
        </Collapse>
      </Box>
    );
  };

  return (
    <Box>
      {sections.map(section => renderSection(section))}
    </Box>
  );
};

export default InventoryPermissions;
